import { useState } from "react";
import { Mail, Loader2, Send, Check } from "lucide-react";
import { toast } from "sonner";
import { createContact } from "@/lib/api";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("All fields are required.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      toast.error("Enter a valid email.");
      return;
    }
    setSending(true);
    try {
      await createContact(form);
      setSent(true);
      toast.success("Message sent — we'll be in touch.");
    } catch (e) {
      toast.error("Something went wrong. Try again.");
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full mt-2 bg-background border border-white/15 px-4 py-3 text-foreground placeholder:text-muted-foreground/60 focus:border-signal outline-none font-sans";
  const label = "font-mono text-[11px] uppercase tracking-widest text-muted-foreground";

  return (
    <div className="max-w-3xl mx-auto px-5 lg:px-10 py-16">
      <span className="font-mono text-xs uppercase tracking-[0.3em] text-signal">Get In Touch</span>
      <h1 className="font-anton text-5xl md:text-7xl uppercase tracking-tight mt-3 leading-none" data-testid="contact-title">
        Contact
      </h1>
      <p className="text-muted-foreground text-lg mt-4">
        Tips, questions, or partnerships — reach the desk directly. You can also email{" "}
        <a href="mailto:peartmedia@gmail.com" className="text-signal hover:underline">peartmedia@gmail.com</a>.
      </p>

      {sent ? (
        <div className="border border-signal bg-card p-10 mt-10 text-center" data-testid="contact-success">
          <Check className="w-8 h-8 text-signal mx-auto" />
          <h2 className="font-anton text-3xl uppercase tracking-tight mt-4">Message Sent</h2>
          <p className="text-muted-foreground mt-3">
            Thanks {form.name.split(" ")[0]} — we've received your message and will reply soon.
          </p>
        </div>
      ) : (
        <div className="border border-white/10 bg-card p-6 md:p-8 mt-10 space-y-5">
          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <label className={label}>Name</label>
              <input data-testid="contact-name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Your name" className={inputCls} />
            </div>
            <div>
              <label className={label}>Email</label>
              <input data-testid="contact-email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@email.com" className={inputCls} />
            </div>
          </div>
          <div>
            <label className={label}>Message</label>
            <textarea
              data-testid="contact-message"
              value={form.message}
              onChange={(e) => set("message", e.target.value)}
              rows={5}
              placeholder="What's on your mind?"
              className={inputCls + " resize-none"}
            />
          </div>
          <button
            data-testid="contact-submit"
            onClick={submit}
            disabled={sending}
            className="inline-flex items-center gap-2 bg-signal text-white font-mono text-xs uppercase tracking-widest px-7 py-4 hover:bg-signal-hover transition-colors disabled:opacity-50"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {sending ? "Sending…" : "Send Message"}
          </button>
        </div>
      )}

      <div className="mt-10 flex items-center gap-3 font-mono text-xs uppercase tracking-widest text-muted-foreground">
        <Mail className="w-4 h-4 text-signal" />
        <a href="mailto:peartmedia@gmail.com" className="hover:text-signal">peartmedia@gmail.com</a>
      </div>
    </div>
  );
}
